import React, { useState } from 'react';
import { ArrowLeft, CheckCircle2, Loader2, Megaphone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { CreativeBody, CREATIVE_TIPOS, ObservationsList, canPreview } from './AdsCreativeParts';
import { adsApprovalsAPI, isClientViewMode } from '../lib/api';

export const KindBadge = ({ kind }) => (kind === 'ad'
  ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 bg-violet-100 text-violet-800"><Megaphone className="w-3 h-3" />Anuncio</span>
  : <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 bg-sky-100 text-sky-800">Publicación de redes</span>);

export function AdApprovalListCard({ c, onOpen }) {
  return (
    <Card className="cursor-pointer hover:shadow-md hover:border-primary transition-[box-shadow,border-color] border-violet-300" onClick={() => onOpen(c.id)} data-testid={`approval-ad-item-${c.id}`}>
      <CardContent className="p-3 space-y-1.5">
        <div className="flex items-center justify-between gap-2"><KindBadge kind="ad" /><span className="text-xs text-muted-foreground">{CREATIVE_TIPOS[c.tipo]}</span></div>
        <p className="font-semibold text-sm truncate">{c.nombre}</p>
        <p className="text-xs text-muted-foreground">Campaña {c.campaign_nombre}{c.batch_nombre ? ` · ${c.batch_nombre}` : ''}</p>
        {c.copy_text && <p className="text-xs text-muted-foreground line-clamp-2">{c.copy_text}</p>}
        <p className="text-xs text-primary font-medium">Revisar anuncio →</p>
      </CardContent>
    </Card>
  );
}

const Actions = ({ c, onDone }) => {
  const [mode, setMode] = useState(null);
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (p, msg) => {
    setBusy(true);
    try { await p; toast.success(msg); onDone(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (isClientViewMode()) return <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="ad-review-readonly-note">Modo vista de cliente: aquí el cliente ve los botones Aprobar y Solicitar cambios. Tú solo puedes mirar.</p>;
  if (!canPreview(c.drive_url)) return <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3" data-testid="ad-review-no-file">Todavía no podemos enseñarte el archivo de este anuncio. Te avisaremos cuando esté listo para revisar.</p>;
  if (mode === 'changes') return (
    <div className="space-y-2">
      <Textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={3} placeholder="¿Qué te gustaría cambiar?" data-testid="ad-review-changes-text" />
      <div className="flex gap-2">
        <Button variant="ghost" onClick={() => setMode(null)}>Cancelar</Button>
        <Button disabled={!texto.trim() || busy} onClick={() => run(adsApprovalsAPI.requestChanges(c.id, texto.trim()), 'Cambios enviados')} data-testid="ad-review-changes-send">Enviar cambios</Button>
      </div>
    </div>
  );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-11" disabled={busy} onClick={() => run(adsApprovalsAPI.approve(c.id), 'Anuncio aprobado')} data-testid="ad-review-approve-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}Aprobar anuncio
      </Button>
      <Button variant="outline" className="h-11" onClick={() => setMode('changes')} data-testid="ad-review-changes-btn"><MessageSquare className="w-4 h-4 mr-1" />Solicitar cambios</Button>
    </div>
  );
};

export function AdApprovalReview({ c, onBack, onDone }) {
  return (
    <div className="space-y-4" data-testid="ad-review">
      <Button variant="ghost" size="sm" onClick={onBack} data-testid="ad-review-back"><ArrowLeft className="w-4 h-4 mr-1" />Volver a Aprobaciones</Button>
      <Card className="border-violet-300">
        <CardContent className="p-5 space-y-4">
          <div className="space-y-1">
            <KindBadge kind="ad" />
            <p className="text-sm text-muted-foreground">Campaña <span className="font-medium text-foreground">{c.campaign_nombre}</span>{c.campaign_objetivo ? ` · ${c.campaign_objetivo}` : ''}{c.batch_nombre ? ` · ${c.batch_nombre}` : ''}</p>
          </div>
          <CreativeBody c={c} idx="review" />
          <ObservationsList items={c.observaciones} />
          <Actions c={c} onDone={onDone} />
        </CardContent>
      </Card>
    </div>
  );
}
